import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useEffect, useMemo, useState } from 'react';
import { Breadcrumbs, Button, GridBleed } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';

import FadeIn from '../utils/fadeIn';
import CabinList from '../components/cabinList/CabinList';
import PriceDetails from '../components/shared/priceDetails/PriceDetails';
import PriceSummary from '../components/shared/priceSummary/PriceSummary';
import GlobalContext from '../context/globalContext';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import MessageContainer from '../components/shared/message/MessageContainer';
import HeadingContainer from '../components/shared/headingContainer/HeadingContainer';
import CruisePackageInfo from '../components/cabinList/CruisePackageInfo';
import { texts } from '../assets/texts';
import { urlUtils } from '../utils/url';

import * as CabinListActions from '../redux/actions/cabinListActions';

import './cabinListContainer.scss';

const CabinListContainer = ({
  accommodation,
  actions,
  cruise,
  initialized,
  labels,
  message,
  priceDetails,
  searchInformation,
  siteSettings,
  transport,
  userId,
}) => {
  const {
    cabins,
    duration: cruiseDuration,
    selectedCabinKey,
    totalDuration,
  } = cruise;

  const [breadCrumbSummaryLink, setBreadCrumbSummaryLink] = useState('');
  const isGlobetrotter = useIsGlobetrotter();
  const isMobile = useIsMobile();

  useEffect(() => {
    actions.init();
  }, [actions]);

  useEffect(() => {
    if (cruise?.selectedCabinKey && breadCrumbSummaryLink === '') {
      const selectedCabin = cabins?.find(
        (cabin) => cabin.key === selectedCabinKey,
      );
      setBreadCrumbSummaryLink(selectedCabin.summaryLink);
    }
  }, [breadCrumbSummaryLink, cabins, cruise, selectedCabinKey]);

  const globals = useMemo(
    () => ({
      labels,
      siteSettings,
    }),
    [siteSettings, labels],
  );

  // If no search has been done.
  // E.g. on cruise details page in inspiration mode.
  if (siteSettings.noSearch) {
    return null;
  }

  if (!initialized) {
    return (
      <LoaderHeading
        heading={texts[siteSettings.siteId].loaderHeadingCabinList}
        topMargin
      />
    );
  }

  const isChangeCabinPage = urlUtils.isChangeCabinPage();
  const productAndPaxInfo = `${labels.productsIncludedParsed}. ${searchInformation?.passengerInfo}`;
  const selectedCabin = cabins?.find((cabin) => cabin.key === selectedCabinKey);

  const renderPriceDetails = () => (
    <PriceDetails
      priceBreakDown={priceDetails}
      searchInformation={searchInformation}
      labels={labels?.iwPriceDetails}
      isSummaryView
    />
  );

  const DividerElement = isMobile ? GridBleed : 'div';

  return (
    <GlobalContext.Provider value={globals}>
      <FadeIn>
        <div className="iw-cabin-list-container">
          <MessageContainer
            message={message}
            user={userId}
            labels={labels}
            style={{ marginBottom: 20 }}
          />

          {isChangeCabinPage &&
            (isGlobetrotter ? (
              <>
                <Breadcrumbs
                  className="iw-cabin-list__breadcrumbs"
                  links={[
                    {
                      name: labels.iwGeneral.breadcrumbComboSummaryLinkLabel,
                      uri: urlUtils.standaloneUrlHandler(breadCrumbSummaryLink),
                      target: null,
                    },
                    {
                      name: labels.changeCabinPageHeading,
                      uri: window.location.href,
                      target: null,
                    },
                  ]}
                />
                <h1 className="iw-cabin-list__change-cabin-heading">
                  {labels.changeCabinPageHeading}
                </h1>
                <div className="iw-cabin-list__heading-divider" />
              </>
            ) : (
              <HeadingContainer>
                <h1 className="iw-cabin-list__change-cabin-heading">
                  {labels.changeCabinPageHeading}
                </h1>
                <Button
                  href={urlUtils.standaloneUrlHandler(breadCrumbSummaryLink)}
                >
                  {labels.iwGeneral.backToSummary}
                </Button>
              </HeadingContainer>
            ))}

          {cabins && selectedCabin && (
            <>
              {!isChangeCabinPage && transport && (
                <CruisePackageInfo
                  duration={labels.packageInfoDuration
                    .replace('{totalDuration}', totalDuration)
                    .replace('{cruiseDuration}', cruiseDuration)}
                  transport={transport}
                  accommodation={accommodation}
                />
              )}
              <CabinList
                cabins={cabins}
                selectedCabin={selectedCabin}
                selectCabin={actions.selectCabin}
                isChangeCabinPage={isChangeCabinPage}
              />
              {isGlobetrotter && !isChangeCabinPage && (
                <DividerElement className="iw-cabin-list__divider" />
              )}
              <PriceSummary
                discounted={priceDetails.isDiscounted}
                labels={{
                  button: isChangeCabinPage
                    ? labels.bookButtonLabelChangeMode
                    : labels.bookButtonLabel,
                  totalPrice: labels.totalPrice,
                  priceDetails: labels.priceDetailsLinkLabel,
                }}
                onNavigate={() => {
                  window.location = urlUtils.standaloneUrlHandler(
                    `/${selectedCabin.summaryLink}`,
                  );
                }}
                productAndPaxInfo={productAndPaxInfo}
                totalPrice={selectedCabin.totalPrice}
                priceDetails={renderPriceDetails()}
              />
            </>
          )}
        </div>
      </FadeIn>
    </GlobalContext.Provider>
  );
};

CabinListContainer.propTypes = {
  accommodation: PropTypes.shape({}).isRequired,
  actions: PropTypes.shape({
    init: PropTypes.func,
    selectCabin: PropTypes.func,
  }).isRequired,
  cruise: PropTypes.shape({
    cabins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectedCabinKey: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    totalDuration: PropTypes.number.isRequired,
  }).isRequired,
  initialized: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    bookButtonLabel: PropTypes.string,
    bookButtonLabelChangeMode: PropTypes.string,
    breadcrumbComboSummaryLinkLabel: PropTypes.string,
    cabinListHeading: PropTypes.string,
    changeCabinPageHeading: PropTypes.string,
    iwGeneral: PropTypes.shape({
      backToSummary: PropTypes.string,
      breadcrumbComboSummaryLinkLabel: PropTypes.string,
    }),
    iwPriceDetails: PropTypes.shape({}),
    packageInfoDuration: PropTypes.string,
    priceDetailsLinkLabel: PropTypes.string,
    productsIncludedParsed: PropTypes.string,
    totalPrice: PropTypes.string,
  }),
  message: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    severity: PropTypes.string,
  }),
  priceDetails: PropTypes.shape().isRequired,
  searchInformation: PropTypes.shape({
    passengerInfo: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    imageDomain: PropTypes.string.isRequired,
    siteId: PropTypes.number.isRequired,
    noSearch: PropTypes.bool,
  }).isRequired,
  transport: PropTypes.shape({}).isRequired,
  userId: PropTypes.string.isRequired,
};

CabinListContainer.defaultProps = {
  labels: {},
  message: null,
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...CabinListActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  return {
    accommodation: state.accommodation,
    cabins: state.cruise.cabins,
    cruise: state.cruise,
    cruiseDuration: state.cruise.duration,
    initialized: state.cruise.initialized,
    labels: state.labels,
    message: state.message,
    priceDetails: state.cruise.priceDetails,
    searchInformation: state.searchInformation,
    selectedCabinKey: state.cruise.selectedCabinKey,
    siteSettings: state.common.siteSettings,
    totalDuration: state.cruise.totalDuration,
    transport: state.transport,
    userId: state.common.userId,
  };
};

export default connect(mapStateToProps, mapActions)(CabinListContainer);
